import { useLocalization } from "@tm/localization"
import { Box } from "@tm/components"
import { useCallback } from "react"
import { LightColoredIcon, LightContrastTypography } from "../../StyledComponents"

type Props = {
    currencyCode?: string
    secondaryCurrencyCode?: string
    showRepairTimesInHours?: boolean
    showWorks?: boolean
    partCount: number
    partCosts: number
    secondaryPartCosts: number
    workCosts?: number
    secondaryWorkCosts?: number
    repairTimeTotal?: number
    showGrossLabel?: boolean
}

export function SummaryDetails(props: Props) {
    const {
        partCosts,
        secondaryPartCosts,
        repairTimeTotal,
        showRepairTimesInHours,
        showGrossLabel,
        partCount,
        workCosts,
        secondaryWorkCosts,
        showWorks,
        currencyCode,
        secondaryCurrencyCode,
    } = props
    const { translateText, formatCurrency, formatNumber } = useLocalization()

    const renderCurrencyParts = useCallback(
        (costs: number, currCode: string, isItalic?: boolean) => {
            return formatCurrency(costs, currCode ?? "-")
                .trim()
                .split(/\s+/, 2)
                .map((part) => (
                    <LightContrastTypography key={part} textAlign="right" fontStyle={isItalic ? "italic" : "unset"}>
                        {part}
                    </LightContrastTypography>
                ))
        },
        [formatCurrency]
    )

    return (
        <Box
            display="grid"
            paddingTop="5px"
            gridTemplateColumns="auto auto 1fr 0.4fr auto auto "
            gridTemplateRows="auto auto auto"
            columnGap="16px"
            data-testid="grid-box"
        >
            {/* Column headers */}
            <Box gridColumn="span 2" />
            <LightContrastTypography variant="label" textAlign="right">
                {translateText(89)}
            </LightContrastTypography>
            <Box />
            <LightContrastTypography gridColumn="span 2" variant="label" textAlign="right">
                {translateText(showGrossLabel ? 13668 : 694)}
            </LightContrastTypography>

            {/* Spacer Row */}
            <Box gridColumn="1 / -1" height="16px" />

            {/* Parts Rows */}
            <LightColoredIcon name="cart-summary" data-testid="cart-summary-icon" />
            <LightContrastTypography>{translateText(90)}</LightContrastTypography>
            <LightContrastTypography textAlign="right">{formatNumber(partCount, 2)}</LightContrastTypography>
            <Box />
            {renderCurrencyParts(partCosts, currencyCode ?? "-")}

            {secondaryPartCosts && (
                <>
                    <Box gridColumn="span 4" />
                    {renderCurrencyParts(secondaryPartCosts, secondaryCurrencyCode ?? "-", true)}
                </>
            )}

            {showWorks && (
                <>
                    {/* Spacer Row */}
                    <Box gridColumn="1 / -1" height="16px" />

                    {/* Works Rows */}
                    <LightColoredIcon name="repairtimes-summary" data-testid="repairtimes-summary-icon" />
                    <LightContrastTypography>{translateText(83)}</LightContrastTypography>
                    <LightContrastTypography textAlign="right">{!!repairTimeTotal && formatNumber(repairTimeTotal, 2)}</LightContrastTypography>
                    <LightContrastTypography>{!!repairTimeTotal && showRepairTimesInHours && translateText(60)}</LightContrastTypography>
                    {renderCurrencyParts(workCosts ?? 0, currencyCode ?? "-")}

                    {secondaryWorkCosts && (
                        <>
                            <Box gridColumn="span 4" />
                            {renderCurrencyParts(secondaryWorkCosts, secondaryCurrencyCode ?? "-", true)}
                        </>
                    )}
                </>
            )}
        </Box>
    )
}
